import BreakpointsService from "@omni/services/breakpoint/breakpoint.service";
import { ImageViewerImage } from "@omni/types/api/product/bff/shared/imageViewerImage.model";
import { ImageViewerVideo } from "@omni/types/api/product/bff/shared/imageViewerVideo.model";
import { BreakpointMaxSizes } from "@omni/types/enums/breakPoint.enum";
import { cn } from "@omni/utils/styles";
import { useEffect, useRef, useState } from "react";
import InnerImageZoom from "react-inner-image-zoom";
import { Navigation, Thumbs } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperCore } from "swiper/types";

import { Col } from "@/components/General/Grid/col.component";
import { SlideThumbnail } from "@/components/Product/ImageViewer/partials/SlideThumbnail/slideThumbnail.component";
import { SwiperNavButton } from "@/components/Product/ImageViewer/partials/SwiperNavButton/swiperNavButton.component";
import { layoutSettings } from "@/components/Product/ImageViewerFullWidth/imageViewerFullWidth.config";
import { useIsBreakpoint } from "@/utils/breakpoints/breakpoints.provider";
import { stopVideo } from "@/utils/imageViewer";
import { getEffectiveLayoutSettings } from "@/utils/swiper.utils";

type Props = {
    className?: string;
    images: Array<ImageViewerImage>;
    videos: Array<ImageViewerVideo>;
    activeIndex: number;
    setActiveIndex: (index: number) => void;
    defaultVideoThumbnailPath?: string;
};

export const ImageViewerFullWidthComponent = ({
    className,
    images,
    videos,
    activeIndex,
    setActiveIndex,
    defaultVideoThumbnailPath
}: Props) => {
    const [mainSwiper, setMainSwiper] = useState<SwiperCore>();
    const [thumbSwiper, setThumbSwiper] = useState<SwiperCore>();

    const { matches: isMedium } = useIsBreakpoint("md");

    const navigationPrevRef = useRef(null);
    const navigationNextRef = useRef(null);

    const amount = images.concat(videos).length;

    useEffect(() => {
        const hashChangeHandler = () => {
            stopVideo(true);
        };

        window.addEventListener("hashchange", hashChangeHandler);

        return () => {
            window.removeEventListener("hashchange", hashChangeHandler);
        };
    }, []);

    useEffect(() => {
        if (mainSwiper && thumbSwiper) {
            if (mainSwiper.activeIndex !== activeIndex) {
                mainSwiper.slideTo(activeIndex);
                thumbSwiper.update();
            }
        }
    }, [activeIndex, mainSwiper, thumbSwiper]);

    return (
        <div
            className={cn(
                "tw:flex tw:flex-col",
                "tw:not-first:grow-0!",
                "tw:max-md:m-0 tw:max-md:justify-between",
                "tw:xl:flex-row",
                className
            )}
        >
            <Col
                xs={12}
                xl={10}
                className={cn(
                    "tw:p-0 tw:flex tw:justify-center tw:items-center",
                    "tw:xl:pr-6"
                )}
            >
                <div className={cn("tw:flex tw:items-center tw:max-w-full")}>
                    {amount > 1 && (
                        <SwiperNavButton
                            className={cn("tw:hidden", "tw:md:block")}
                            innerRef={navigationPrevRef}
                            disabled={activeIndex === 0}
                            icon={"left"}
                        />
                    )}
                    <Swiper
                        modules={[Thumbs, Navigation]}
                        onSwiper={setMainSwiper}
                        initialSlide={activeIndex}
                        onActiveIndexChange={(swiper) => {
                            setActiveIndex(swiper.activeIndex);
                            stopVideo(true);
                        }}
                        thumbs={{ swiper: thumbSwiper }}
                        allowTouchMove
                        zoom={false}
                        navigation={{
                            nextEl: navigationNextRef.current,
                            prevEl: navigationPrevRef.current
                        }}
                        slidesPerView={"auto"}
                        spaceBetween={5}
                    >
                        {images
                            ?.filter((img) => {
                                const src = !isMedium ? img.main : img.zoom;
                                return !!src;
                            })
                            .map((image, index) => (
                                <SwiperSlide
                                    className={cn(
                                        "tw:flex tw:justify-center tw:items-center"
                                    )}
                                    key={`imageViewerFullWidth-main${index}`}
                                >
                                    <InnerImageZoom
                                        src={
                                            !isMedium
                                                ? image.main!
                                                : image.zoom!
                                        }
                                        className={cn(
                                            "tw:w-[90%]! tw:max-h-[unset]!",
                                            "tw:[&_.iiz__img]:w-full tw:[&_.iiz__img]:max-h-[unset] tw:[&_.iiz__img]:object-contain"
                                        )}
                                        zoomSrc={
                                            !isMedium
                                                ? image.zoom
                                                : image.maxZoom
                                        }
                                        imgAttributes={{
                                            alt: image.altText || ""
                                        }}
                                        zoomPreload={true}
                                        fullscreenOnMobile={true}
                                        hasSpacer={true}
                                        mobileBreakpoint={BreakpointsService.getBreakpointMaxValues(
                                            BreakpointMaxSizes.md
                                        )}
                                    />
                                </SwiperSlide>
                            ))}
                        {videos?.map((video, index) => (
                            <SwiperSlide
                                key={`imageViewerFullWidth-main-video${index}`}
                                className={cn(
                                    "tw:flex tw:justify-center tw:items-center tw:w-full tw:p-0 tw:overflow-hidden",
                                    "tw:before:pt-[56.25%] tw:before:block tw:before:content-['']"
                                )}
                            >
                                <iframe
                                    className={cn(
                                        "tw:block tw:border-none tw:w-full tw:h-full tw:absolute tw:top-0 tw:left-0"
                                    )}
                                    id={`imageViewerFullWidthIframe-${index}`}
                                    src={`${video.main}`}
                                />
                            </SwiperSlide>
                        ))}
                    </Swiper>
                    {amount > 1 && (
                        <SwiperNavButton
                            className={cn("tw:hidden tw:md:block")}
                            innerRef={navigationNextRef}
                            disabled={activeIndex + 1 === amount}
                            icon={"right"}
                        />
                    )}
                </div>
            </Col>
            <Col
                xs={12}
                xl={2}
                className={cn("tw:p-0", "tw:max-md:m-0 tw:max-md:grow-0")}
            >
                <div
                    className={cn(
                        "tw:relative tw:max-h-137.5 tw:mt-5 tw:flex tw:items-center",
                        "tw:xl:items-start"
                    )}
                >
                    {amount > 1 && (
                        <SwiperNavButton
                            disabled={activeIndex === 0}
                            icon={"left"}
                            onClick={() => {
                                if (activeIndex !== 0) {
                                    mainSwiper?.slidePrev();
                                    thumbSwiper?.update();
                                }
                            }}
                            className={cn(
                                "tw:xl:absolute tw:xl:left-1/2 tw:xl:-translate-y-1/2 tw:xl:-translate-x-1/2 tw:xl:-top-3.75 tw:xl:rotate-90"
                            )}
                        />
                    )}
                    <Swiper
                        modules={[Thumbs, Navigation]}
                        className={cn("tw:w-full")}
                        onSwiper={setThumbSwiper}
                        spaceBetween={10}
                        breakpoints={getEffectiveLayoutSettings(layoutSettings)}
                        watchSlidesProgress={true}
                    >
                        {images &&
                            videos &&
                            images.concat(videos).map((media, index) => (
                                <SwiperSlide
                                    className={cn(
                                        "tw:w-fit tw:h-fit tw:max-w-full tw:max-h-full tw:items-center tw:text-center",
                                        {
                                            "tw:border tw:border-grey-light":
                                                index === activeIndex
                                        }
                                    )}
                                    key={`imageViewerFullWidth-thumbs${index}`}
                                    onClick={() => {
                                        setActiveIndex(index);
                                    }}
                                >
                                    <SlideThumbnail
                                        lazyLoad={index > 0}
                                        media={media}
                                        defaultVideoThumbnailPath={
                                            defaultVideoThumbnailPath
                                        }
                                    />
                                </SwiperSlide>
                            ))}
                    </Swiper>
                    {amount > 1 && (
                        <SwiperNavButton
                            disabled={activeIndex + 1 === amount}
                            icon={"right"}
                            onClick={() => {
                                if (activeIndex + 1 !== amount) {
                                    mainSwiper?.slideNext();
                                    thumbSwiper?.update();
                                }
                            }}
                            className={cn(
                                "tw:xl:absolute tw:xl:left-1/2 tw:xl:-translate-y-1/2 tw:xl:-translate-x-1/2 tw:xl:top-full tw:xl:mt-3.75  tw:xl:rotate-90"
                            )}
                        />
                    )}
                </div>
            </Col>
        </div>
    );
};
